import {createSelector} from '@reduxjs/toolkit';
import SunCalc from 'suncalc';

import {solarDimmProfileOptions} from './dimProfiles';
import {selectLanguage, selectLightDistribution, selectSpcAttributes, selectSpcConfiguration} from '../selectors';
import {solarActivityLevelOptions, solarAzimuthOptions, solarShadowLevelOptions} from './solarSectionConfig';
import {getValueOfFeature, isSatisfied} from '../util/values';
import {calculatePower} from '../util/dimLevel';
import {daysInMonth} from '../util/daysInMonth';
import {sunlightPercentage} from '../util/sunlightPercentage';
import {initialCalculated, initialSelfSufficiency} from './solarSlice';

// Solar Configuration
export const selectSolarUseCase = state => state.solar.data.project?.useCase;
export const selectSolarUseCaseIsSatisfied = state => state.solar.data.project?.useCase !== null;
export const selectSolarActivityLevel = state => state.solar.data.project?.activityLevel;
export const selectSolarActivityLevelIsSatisfied = state => state.solar.data.project?.activityLevel !== null;
export const selectSolarShadowLevel = state => state.solar.data.project?.shadowLevel;
export const selectSolarShadowLevelIsSatisfied = state => state.solar.data.project?.shadowLevel !== null;

export const selectSolarAzimuth = state => state.solar.data.project?.azimuth ?? 's';
export const selectSolarAzimuthIsSatisfied = state => state.solar.data.project?.azimuth !== null;
export const selectSolarLocation = state => state.solar.data.project?.location;
export const selectSolarLocationIsSatisfied = state => state.solar.data.project?.location !== null;
export const selectSolarBatteryType = state => state.solar.data.project?.batteryType;
export const selectSolarBatteryTypeIsSatisfied = state => state.solar.data.project?.batteryType !== null;
//export const selectSolarTemperature = state => state.solar.data.project?.temperature;
//export const selectSolarTemperatureIsSatisfied = state => state.solar.data.project?.temperature !== null;
export const selectSolarDimmProfile = state => state.solar.data.project?.dimmProfile;
export const selectSolarDimmProfileIsSatisfied = state => state.solar.data.project?.dimmProfile !== null;

export const selectSolarLuminarPower = state => state.solar.data.project?.luminarPower ?? 22;
export const selectSolarBatteryReserve = state => state.solar.data.project?.batteryReserve ?? 0.2;
export const selectSolarBatteryVoltage = state => state.solar.data.project?.batteryVoltage ?? 12;
export const selectSolarElectricLoss = state => state.solar.data.project?.electricLoss ?? 0.9;
export const selectSolarPVReserve = state => state.solar.data.project?.pvReserve ?? 0.5;

export const winterDate = new Date('2024-12-21T12:00:00');
export const springDate = new Date('2024-10-15T12:00:00');
export const summerDate = new Date('2024-09-15T12:00:00');

export const selectSolarCalculated = state => state.solar.data.project?.calculated ?? initialCalculated;

export const selectSolarPVOrientations = state => state.solar.data.project.orientation;

export const selectSolarSelfSufficiency = state => state.solar.data.project.selfSufficiency ?? initialSelfSufficiency;

export const selectSolarDimmProfileData = createSelector(
	[selectSolarDimmProfile],
	(dimmProfileId) => solarDimmProfileOptions.find(option => option.id === dimmProfileId)
);
export const selectSolarActivityLevelFactor = createSelector(
	[selectSolarActivityLevel],
	(activityLevel) => solarActivityLevelOptions.find(option => option.id === activityLevel).factor
);
export const selectSolarActivityLevelData = createSelector(
	[selectSolarActivityLevel],
	(activityLevel) => solarActivityLevelOptions.find(option => option.id === activityLevel)
);
export const selectSolarShadowLevelFactor = createSelector(
	[selectSolarShadowLevel],
	(shadowLevel) => solarShadowLevelOptions.find(option => option.id === shadowLevel).factor
);
export const selectSolarShadowLevelData = createSelector(
	[selectSolarShadowLevel],
	(shadowLevel) => solarShadowLevelOptions.find(option => option.id === shadowLevel)
);
export const selectSolarAzimuthAngle = createSelector(
	[selectSolarAzimuth],
	(azimuth) => solarAzimuthOptions.find(option => option.id === azimuth).angle
);
export const selectSolarAzimuthData = createSelector(
	[selectSolarAzimuth],
	(azimuth) => solarAzimuthOptions.find(option => option.id === azimuth)
);
export const selectSolarUnitHeight = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LU_SolarUnitHeight', spcConfiguration)
	)
);
export const selectSolarUnitHeightIsSatisfied = createSelector([selectSpcAttributes], (spcAttributes) => (
		isSatisfied('LU_SolarUnitHeight', spcAttributes)
	)
);
export const selectSolarGenerators = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LU_SolarUnitHeight', spcConfiguration)
	)
);

export const selectSolarLumHeight1 = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LE_LuminairePositionHeight_1', spcConfiguration)
	)
);
export const selectSolarLumHeight2 = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LE_LuminairePositionHeight_2', spcConfiguration)
	)
);
export const selectSolarHousingNumber = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LU_Housing_LegacyArticleNumberGerman', spcConfiguration)
	)
);
export const selectSolarBatteryNumber = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LU_Battery_LegacyArticleNumberGerman', spcConfiguration)
	)
);
export const selectSolarUnitNumber = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LU_SolarUnit_LegacyArticleNumberGerman', spcConfiguration)
	)
);
export const selectSolarNumber1 = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LU_LegacyArticleNumberGerman_1', spcConfiguration)
	)
);
export const selectSolarNumber2 = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LU_LegacyArticleNumberGerman_2', spcConfiguration)
	)
);

export const selectSolarPrice = state => state.price?.data.totalPriceInEuro;
export const selectSolarBatteryPrice = state => state.price?.data.conditionalPrices.find(cp => cp.description === 'Battery')?.priceInEuro;
export const selectSolarLuminairePrice = state => state.price?.data.conditionalPrices.find(cp => cp.description === 'Luminaire')?.priceInEuro;
export const selectSolarUnitPrice = state => state.price?.data.conditionalPrices.find(cp => cp.description === '')?.priceInEuro;


const luminarCountValues = {
	'1head': 1,
	'2heads': 2
};
export const selectSolarLuminarCountValue = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		luminarCountValues[getValueOfFeature('HS_Construction', spcConfiguration)]
	)
);
export const selectSolarLumPosition2IsVisible = createSelector(
	[selectSolarLuminarCountValue],
	(lumCount) => lumCount === 2);

export const selectSolarVersion = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('HS_HousingSize', spcConfiguration) ?? 'AetoPro'
	)
);
export const selectSolarHeight = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('HS_HousingHeight', spcConfiguration)
	)
);
export const selectSolarBatterySize = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LU_Battery', spcConfiguration)
	)
);
export const selectColourTemperature = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('LE_ColourTemperature_1', spcConfiguration)
	)
);
export const selectSolarBase = createSelector([selectSpcConfiguration], (spcConfiguration) => (
		getValueOfFeature('HS_Base', spcConfiguration)
	)
);

export const selectSolarLumPosition1 = createSelector([selectSolarLumHeight1, selectSolarHeight], (lumHeight, housingHeight) => {
		const lumHeightOverBase = parseInt(lumHeight, 10) - parseInt(housingHeight, 10);
		return lumHeightOverBase / 10;
	}
);
export const selectSolarLumPosition1IsSatisfied = createSelector([selectSolarLumPosition1], (lumPos) => (!isNaN(lumPos))
);
export const selectSolarLumPosition2 = createSelector([selectSolarLumHeight2, selectSolarHeight], (lumHeight, housingHeight) => {
		const lumHeightOverBase = parseInt(lumHeight, 10) - parseInt(housingHeight, 10);
		return lumHeightOverBase / 10;
	}
);
export const selectSolarLumPosition2IsSatisfied = createSelector([selectSolarLumPosition2], (lumPos) => (!isNaN(lumPos))
);


const batterySizes = {
	Type1: 26,
	Type2: 52
};
export const selectMaxUsableBatteryCapacity = createSelector(
	[selectSolarBatterySize, selectSolarBatteryReserve, selectSolarElectricLoss, selectSolarBatteryVoltage],
	(batterySize, batteryReserve, electricLoss, batteryVoltage) =>
		Math.round(batterySizes[batterySize] * batteryVoltage * electricLoss * batteryReserve * 10) / 10
);

export const selectSolarDimmProfilePlotDataBySeason = createSelector(
	[selectSolarDimmProfileData, selectSolarLocation, selectSolarActivityLevelFactor, selectSolarLuminarCountValue, selectSolarVersion, selectLanguage],
	(dimmProfileData, location, activityLevel, luminarCount, version, lang) => (
		{
			winter: solarDimmProfilePlotlyData(
				winterDate,
				dimmProfileData,
				location,
				activityLevel,
				luminarCount,
				version,
				lang),
			spring: solarDimmProfilePlotlyData(
				springDate,
				dimmProfileData,
				location,
				activityLevel,
				luminarCount,
				version,
				lang),
			summer: solarDimmProfilePlotlyData(
				summerDate,
				dimmProfileData,
				location,
				activityLevel,
				luminarCount,
				version,
				lang)
		}
	)
);

export const selectSolarPowerRequirementsBySeason = createSelector(
	[selectSolarDimmProfilePlotDataBySeason, selectSolarLuminarPower, selectSolarLuminarCountValue],
	(dimmProfilePlotDataBySeason, luminarPower, lumCount) => (
		{
			winter: power(dimmProfilePlotDataBySeason.winter, luminarPower, lumCount),
			spring: power(dimmProfilePlotDataBySeason.spring, luminarPower, lumCount),
			summer: power(dimmProfilePlotDataBySeason.summer, luminarPower, lumCount),
		}
	)
);


export const selectSolarPVPlotData1 = createSelector(
	[selectSolarPVOrientations, selectSolarLuminarCountValue, selectSolarShadowLevelFactor, selectSolarPVReserve],
	(orientations, luminars, shadowLevel, pvReserve) => solarPVPlotData(
		orientations,
		1,
		1,
		shadowLevel,
		pvReserve
	) // one generator not possible with two luminars
);

export const selectSolarPVPlotData2 = createSelector(
	[selectSolarPVOrientations, selectSolarLuminarCountValue, selectSolarShadowLevelFactor, selectSolarPVReserve],
	(orientations, luminars, shadowLevel, pvReserve) => solarPVPlotData(
		orientations,
		2,
		luminars,
		shadowLevel,
		pvReserve)
);
export const selectSolarPVPlotData3 = createSelector(
	[selectSolarPVOrientations, selectSolarLuminarCountValue, selectSolarShadowLevelFactor, selectSolarPVReserve],
	(orientations, luminars, shadowLevel, pvReserve) => solarPVPlotData(
		orientations,
		3,
		luminars,
		shadowLevel,
		pvReserve),
);
export const selectSolarPVPlotData4 = createSelector(
	[selectSolarPVOrientations, selectSolarLuminarCountValue, selectSolarShadowLevelFactor, selectSolarPVReserve],
	(orientations, luminars, shadowLevel, pvReserve) => solarPVPlotData(
		orientations,
		4,
		luminars,
		shadowLevel,
		pvReserve),
);



function getPvInFromPlot(plotData) {
	return {
		winter: plotData && plotData[0] && plotData[0].y ? Math.round(plotData[0].y[11] * 10) / 10 : 0,
		spring: plotData && plotData[0] && plotData[0].y ? Math.round(plotData[0].y[9] * 10) / 10 : 0,
		summer: plotData && plotData[0] && plotData[0].y ? Math.round(plotData[0].y[6] * 10) / 10 : 0,
	};
}
export const selectSolarPVMaxInput1 = createSelector(
	[selectSolarPVPlotData1],
	(plotData) => getPvInFromPlot(plotData)
);
export const selectSolarPVMaxInput2 = createSelector(
	[selectSolarPVPlotData2],
	(plotData) => getPvInFromPlot(plotData)
);
export const selectSolarPVMaxInput3 = createSelector(
	[selectSolarPVPlotData3],
	(plotData) => getPvInFromPlot(plotData)
);
export const selectSolarPVMaxInput4 = createSelector(
	[selectSolarPVPlotData4],
	(plotData) => getPvInFromPlot(plotData)
);
export const selectSolarPVMaxInput = createSelector(
	[selectSolarPVMaxInput1, selectSolarPVMaxInput2, selectSolarPVMaxInput3, selectSolarPVMaxInput4],
	(pvIn1, pvIn2, pvIn3, pvIn4) => (
		[pvIn1, pvIn2, pvIn3, pvIn4]
	)
);


export const selectRestrictionDimmingFactorBySeason = createSelector(
	[selectSolarPowerRequirementsBySeason, selectSolarPVMaxInput, selectSolarGenerators, selectMaxUsableBatteryCapacity],
	(solarPowerRequirementsBySeason, solarPVMaxInput, generators, maxUsableBatteryCapacity) => ({
		winter: getRestrictionDimmingFactor(solarPowerRequirementsBySeason.winter, solarPVMaxInput[generators - 1]?.winter, maxUsableBatteryCapacity),
		spring: getRestrictionDimmingFactor(solarPowerRequirementsBySeason.spring, solarPVMaxInput[generators - 1]?.spring, maxUsableBatteryCapacity),
		summer: getRestrictionDimmingFactor(solarPowerRequirementsBySeason.summer, solarPVMaxInput[generators - 1]?.summer, maxUsableBatteryCapacity)
	})
);

function getRestrictionDimmingFactor(solarPowerRequirementsBySeason, solarPVMaxInput, maxUsableBatteryCapacity) {
	const usableInput = Math.min(solarPVMaxInput, maxUsableBatteryCapacity);
	const usedInputFactor = solarPowerRequirementsBySeason / usableInput;
	return Math.min(1, 1 / usedInputFactor);
}

export const selectSolarMaxLumen = createSelector(
	[selectColourTemperature],
	(temperature) => {
		switch (temperature) {
			case '2000K3000K':
				return 3982;
			case 'Red2700K':
				return 3820;
			case '2200K':
				return 3820; // 181 lm/W * 22
			case '2700K':
				return 3982; // 191 lm/W * 22
			case '3000K':
				return 4422; // 201 lm/W * 22
			case '4000K':
				return 4642; // 211 lm/W * 22
			default:
				return 4422;
		}
	}
);

// helpers

function solarPVPlotData(orientations, generators, luminars, shadowLevel, pvReserve) {
	if (!orientations) return null;

	let assembly = {
		front: generators,
		left: generators,
		right: generators,
		back: generators,
	};
	// how many panels in which orientation
	if (luminars === 1) {
		assembly = {
			front: generators,
			left: generators,
			right: generators,
			back: generators - 1,
		};
	} else if (luminars === 2) {
		assembly = {
			front: generators - 1,
			left: generators,
			right: generators,
			back: generators - 1,
		};
	}

	const front = orientations.front;
	const back = orientations.back;
	const left = orientations.left;
	const right = orientations.right;
	const pvgisAvailable = front.pvgisMonthly !== null || back.pvgisMonthly !== null || left.pvgisMonthly !== null || right.pvgisMonthly !== null;
	if (!pvgisAvailable) return null;

	const x = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
	const y = x.map((name, index) => (
		(
			(front.pvgisMonthly ? front.pvgisMonthly[index].P * assembly.front : 0) +
			(back.pvgisMonthly ? back.pvgisMonthly[index].P * assembly.back : 0) +
			(left.pvgisMonthly ? left.pvgisMonthly[index].P * assembly.left : 0) +
			(right.pvgisMonthly ? right.pvgisMonthly[index].P * assembly.right : 0)
		)
		* shadowLevel
		/ daysInMonth(index)
	));
	return [
		{
			x: x,
			y: y,
			fill: 'tozeroy',
			fillcolor: '#0ED45D',
			type: 'scatter',
			mode: 'none'
		},
	];
}

function solarDimmProfilePlotlyData(date, dimmProfileData, location, activityLevelFactor, luminarCount, version, lang) {

	if (!location) return null;

	const sunTimes = SunCalc.getTimes(date, location.latitude, location.longitude);
	const sunriseTs = Date.parse(sunTimes.sunrise.toLocaleString('en-US', {
		dateStyle: 'medium',
		timeStyle: 'medium',
		timeZone: location.timezoneName,
		hour12: false
	}));
	const sunsetTs = Date.parse(sunTimes.sunset.toLocaleString('en-US', {dateStyle: 'medium', timeStyle: 'medium', timeZone: location.timezoneName, hour12: false}));
	const sunrise = new Date(sunriseTs).toLocaleTimeString(lang, {hour12: false}).toUpperCase();
	const sunset = new Date(sunsetTs).toLocaleTimeString(lang, {hour12: false}).toUpperCase();
	// get sunlight mask and shift sunlight array by 12 hours
	const sunlight = sunlightPercentage(sunrise, sunset);
	const shiftedSunlight = sunlight.slice(12).concat(sunlight.slice(0, 12));
	const x = ['12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
	const y = x.map((name, index) =>
		// calculate dimm profile based on selected options
		(
			(dimmProfileData[version][index].level * (1 - shiftedSunlight[index]) * (1 - activityLevelFactor) +
				dimmProfileData[version][index].activity * (1 - shiftedSunlight[index]) * activityLevelFactor)
		)
	);
	return [
		{
			x: x,
			y: y,
			type: 'bar',
			color: '#fff',
			marker: {
				color: '#fff',
				opacity: 1,
			},
			width: 1
		},
	];
}

function power(plotData, luminarPower, lumCount) {
	if (plotData[0] && plotData[0].y && plotData[0].y.length > 0) {
		// sum over all values of plotData.y
		return Math.round(plotData[0].y.reduce((a, b) => a + (b * luminarPower * lumCount), 0) * 10) / 10;
	} else {
		return null;
	}
}
